import { axiosPrivate } from '../../api/axios';
import { getSocket } from '../../services/socket';
import {
    setLoading,
    setChatData,
    setMessages,
    prependMessages,
    setUnreadCounts,
} from '../reducers/chatRealtime';

export const MESSAGES_PAGE_SIZE = 15;

const withSocketId = (config = {}) => {
    const socketId = getSocket()?.id;
    if (!socketId) return config;
    return {
        ...config,
        params: {
            ...(config.params || {}),
            socketId,
        },
    };
};

export const fetchCourseEntry = (courseId) => async () => {
    const response = await axiosPrivate.get(
        `/api/chat-realtime/${courseId}/entry`,
        withSocketId({
            validateStatus: (status) => status === 200 || status === 403 || status === 409,
        }),
    );
    return response.data;
};

export const fetchChatRealtime = (courseId) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await axiosPrivate.get(
            `/api/chat-realtime/${courseId}`,
            withSocketId(),
        );
        if (response.status === 200) {
            dispatch(setChatData(response.data));
        }
    } catch (error) {
        if (error.response?.status === 404) {
            await dispatch(provisionChatRealtime(courseId));
            try {
                const retry = await axiosPrivate.get(
                    `/api/chat-realtime/${courseId}`,
                    withSocketId(),
                );
                if (retry.status === 200) {
                    dispatch(setChatData(retry.data));
                }
            } catch {
                // provisionChatRealtime already stored chat + participants
            }
        } else if (
            error.response?.data?.reason === 'unpaid'
            || error.response?.data?.reason === 'account_limit'
        ) {
            throw error;
        }
    } finally {
        dispatch(setLoading(false));
    }
};

export const provisionChatRealtime = (courseId) => async (dispatch) => {
    const response = await axiosPrivate.post(`/api/chat-realtime/provision/${courseId}`);
    if (response.status === 201) {
        dispatch(setChatData({
            chat: response.data.chat,
            conversations: [response.data.conversation],
            participants: response.data.participants || [],
            onlineUsers: response.data.onlineUsers || [],
        }));
    }
};

export const fetchMessages = (conversationId, cursor = null) => async (dispatch) => {
    const params = { limit: MESSAGES_PAGE_SIZE };
    if (cursor) params.cursor = cursor;

    const response = await axiosPrivate.get(
        `/api/chat-realtime/messages/${conversationId}`,
        { params },
    );

    if (response.status === 200) {
        if (cursor) {
            dispatch(prependMessages(response.data.messages));
        } else {
            dispatch(setMessages(response.data.messages));
        }
        return response.data;
    }
    return null;
};

export const fetchUnreadCounts = () => async (dispatch) => {
    const response = await axiosPrivate.get('/api/chat-realtime/read-status/unread');
    if (response.status === 200) {
        dispatch(setUnreadCounts(response.data.members));
    }
};

export const markConversationRead = (conversationId, messageId) => async () => {
    await axiosPrivate.post(`/api/chat-realtime/read-status/${conversationId}`, {
        messageId,
    });
};

export const registerFcmDevice = (fcmToken, platform = 'web') => async () => {
    await axiosPrivate.post('/api/chat-realtime/devices', { fcmToken, platform });
};

export const getPresignedUploadUrl = (courseId, { fileName, mimeType, size }) => async () => {
    const response = await axiosPrivate.post('/api/chat-realtime/uploads/presign', {
        courseId,
        fileName,
        mimeType,
        size,
    });
    return response.data;
};

export const sendMessageRest = (conversationId, { content, type = 'text', attachments = [], replyToMessageId, clientId, mentionIds }) => async () => {
    const response = await axiosPrivate.post(
        `/api/chat-realtime/messages/${conversationId}`,
        { content, type, attachments, replyToMessageId, clientId, mentionIds },
    );
    if (response.status === 201) {
        return response.data.message;
    }
    return null;
};
